import React, { useState } from 'react'

import { Translate } from '@hozana/intl/Translate'
import { nl2br } from '@hozana/utils/functions/strings'

import { Button } from 'elements/button/Button'
import { ALERT_COLORS } from 'elements/constants'
import { Block } from 'elements/layout/Block'
import { Div } from 'elements/layout/Div'
import { Span } from 'elements/layout/Span'
import { P } from 'elements/text/P'
import { Alert } from 'elements/ui/Alert'
import { Help } from 'elements/ui/Help'
import { Label } from 'elements/ui/Label'

import { ICON } from 'config/icons'

import type { TCommunity } from 'modules/community/types'
import { TagsFormAdmin } from 'modules/tag/components/TagsFormAdmin'

import { CommunityLiveForm } from './CommunityLiveForm'
import { DisplayModeSelector } from './DisplayModeSelector'

export type TCommunityAdminFormProps = {
  community: TCommunity
}

export const CommunityAdminForm: TFC<TCommunityAdminFormProps> = ({ community }) => {
  const [isAdminFormVisible, setIsAdminFormVisible] = useState(true)
  const [isLiveExplanationsDisplayed, setLiveExplanationsDisplayed] = useState(false)

  const rateQuantile = -(community.correctedReadRateQuantile - 11) * 10
  let colors: keyof typeof ALERT_COLORS = 'neutral'
  if ([10, 20].includes(rateQuantile)) colors = 'success'
  if ([30, 40, 50].includes(rateQuantile)) colors = 'info'

  return (
    <>
      <Div p="10px 0 0" maxW={{ xs: 'initial', md: '712px', lg: 'initial' }} m="auto">
        <Div align="right">
          <Button
            colors="greyTransparent"
            color="black30"
            icon={ICON.SETTINGS}
            size="small"
            m="0 0 5px"
            onClick={() => setIsAdminFormVisible(!isAdminFormVisible)}
          >
            {isAdminFormVisible ? 'Masquer' : 'Admin'}
          </Button>
        </Div>
        {isAdminFormVisible && (
          <Block m="0 0 1em" p="10px" square>
            <P align="center" m="0 0 5px 0" fontSize="1.6em" bold>
              Administration
            </P>
            <P m="0 0 5px 0" bold>
              Tags
            </P>
            <TagsFormAdmin community={community} />
            <P m="1em 0 0 0" bold>
              Statistiques
            </P>
            <P m="5px 0" italic>
              <Translate
                values={{
                  percent: (community.readRate * 100).toFixed(1),
                }}
              >
                trans:community:community.admin-statistics.raw-read-rate
              </Translate>
              <br />
              <Translate
                values={{
                  percent: (community.correctedReadRate * 100).toFixed(1),
                }}
              >
                trans:community:community.admin-statistics.corrected-read-rate
              </Translate>
              &nbsp;
              <Label colors={colors}>
                {community.correctedReadRateQuantile
                  ? rateQuantile <= 50
                    ? `Top ${rateQuantile}%`
                    : 'Taux faible'
                  : 'Donnée inexistante'}
              </Label>
            </P>
            <P m="1em 0 5px 0" bold>
              Visibilité
            </P>
            <DisplayModeSelector m="10px" community={community} />
            <P m="1em 0 0 0" bold>
              Live
              <Span right>
                <Help onClick={() => setLiveExplanationsDisplayed(!isLiveExplanationsDisplayed)} />
              </Span>
            </P>
            <CommunityLiveForm community={community} />
            {isLiveExplanationsDisplayed && (
              <Alert status="info">
                {nl2br(
                  `Choisissez "Live" pour les communautés qui doivent publier à une heure précise (par exemple, les Vèpres, à 17h).\nLes publications ne paraitrons pas dans le Mail Digest (Mail du Coin-Prière) du lendemain.`,
                )}
              </Alert>
            )}
          </Block>
        )}
      </Div>
    </>
  )
}
